import { formatRupiah } from '@safepos/shared';
import { Alert, Button, Card, PageHeader, SelectField, TextField } from '@safepos/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, download } from '../lib/api';
import { useSubmit } from '../lib/useForm';

type Cell = string | number | null;
interface Col {
  key: string;
  label: string;
  type: 'text' | 'int' | 'money' | 'qty' | 'pct';
}
interface Table {
  name: string;
  title: string;
  columns: Col[];
  rows: Record<string, Cell>[];
  totals?: Record<string, Cell>;
  meta: { from: string; to: string; note?: string };
}

const REPORTS = [
  { id: 'sales-summary', label: 'Ringkasan penjualan', group: true },
  { id: 'sales-by-product', label: 'Per produk' },
  { id: 'sales-by-category', label: 'Per kategori' },
  { id: 'sales-by-employee', label: 'Per kasir' },
  { id: 'sales-by-payment', label: 'Per metode bayar' },
  { id: 'sales-by-hour', label: 'Per jam' },
  { id: 'gross-profit', label: 'Laba kotor', group: true },
  { id: 'shifts', label: 'Shift & kas' },
  { id: 'inventory', label: 'Inventori', outlet: true },
  { id: 'negative-stock', label: 'Stok negatif' },
  { id: 'sync-issues', label: 'Galat sinkron' },
] as const;

const iso = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86_400_000);
const today = () => iso(new Date(Date.now() + 7 * 3600_000)); // WIB

export const fmtCell = (c: Col, v: Cell) => {
  if (v == null || v === '') return '—';
  if (c.type === 'money') return formatRupiah(Number(v));
  if (c.type === 'qty')
    return new Intl.NumberFormat('id-ID', { maximumFractionDigits: 3 }).format(Number(v));
  if (c.type === 'int') return new Intl.NumberFormat('id-ID').format(Number(v));
  if (c.type === 'pct') return `${Number(v)}%`;
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(v)))
    return new Date(String(v)).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' });
  return String(v);
};

/** Diagram batang sederhana (SVG, tanpa pustaka). */
export function Bars({
  data,
  height = 120,
  format = (n: number) => formatRupiah(n),
}: {
  data: { label: string; value: number }[];
  height?: number;
  format?: (n: number) => string;
}) {
  const max = Math.max(1, ...data.map((d) => d.value));
  const w = 100 / Math.max(1, data.length);
  return (
    <svg
      viewBox={`0 0 100 ${height / 4}`}
      className="w-full"
      style={{ height }}
      role="img"
      aria-label="Diagram batang"
    >
      {data.map((d, i) => {
        const h = (d.value / max) * (height / 4 - 6);
        return (
          <g key={i}>
            <title>{`${d.label}: ${format(d.value)}`}</title>
            <rect
              x={i * w + w * 0.12}
              y={height / 4 - 5 - h}
              width={w * 0.76}
              height={Math.max(h, d.value > 0 ? 0.4 : 0)}
              rx={0.6}
              fill="currentColor"
              className="text-brand-700"
            />
            <text
              x={i * w + w / 2}
              y={height / 4 - 0.8}
              fontSize={2.6}
              textAnchor="middle"
              fill="#64748b"
            >
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function ReportsPage() {
  const [name, setName] = useState<(typeof REPORTS)[number]['id']>('sales-summary');
  const [from, setFrom] = useState(iso(addDays(new Date(`${today()}T00:00:00Z`), -6)));
  const [to, setTo] = useState(today());
  const [outletId, setOutletId] = useState('');
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');
  const def = REPORTS.find((r) => r.id === name)!;
  const outlets = useQuery({
    queryKey: ['outlets'],
    queryFn: () => api.get<{ id: string; name: string }[]>('/outlets'),
  });
  const effOutlet =
    'outlet' in def && def.outlet && !outletId ? (outlets.data?.[0]?.id ?? '') : outletId;
  const qs = `from=${from}&to=${to}&groupBy=${groupBy}${effOutlet ? `&outletIds=${effOutlet}` : ''}`;
  const q = useQuery({
    queryKey: ['report', name, qs],
    queryFn: () => api.get<Table>(`/reports/${name}?${qs}`),
    retry: false,
  });
  const exp = useSubmit(async (format: 'csv' | 'xlsx' | 'pdf', path = `${name}/export`) => {
    await download(
      'tenant',
      `/reports/${path}?${qs}&format=${format}`,
      `laporan-${name}-${from}_${to}.${format}`,
    );
  });
  const preset = (days: number | 'month') => {
    const t = today();
    setTo(t);
    setFrom(
      days === 'month'
        ? `${t.slice(0, 7)}-01`
        : iso(addDays(new Date(`${t}T00:00:00Z`), -(days - 1))),
    );
  };
  const t = q.data;
  const money = t?.columns.find((c) => c.key === 'net' || c.key === 'profit');
  const chart =
    t && (name === 'sales-summary' || name === 'sales-by-hour' || name === 'gross-profit')
      ? t.rows.map((r) => ({
          label: String(r[name === 'sales-by-hour' ? 'hour' : 'bucket']).slice(
            name === 'sales-by-hour' ? 0 : -5,
            name === 'sales-by-hour' ? 2 : undefined,
          ),
          value: Number(r[name === 'gross-profit' ? 'profit' : 'net'] ?? 0),
        }))
      : null;
  return (
    <>
      <PageHeader
        title="Laporan"
        subtitle="Dihitung langsung dari transaksi sumber, menurut zona waktu tiap outlet"
      />
      <div className="grid gap-4 lg:grid-cols-[14rem_1fr]">
        <nav aria-label="Jenis laporan" className="flex gap-1 overflow-x-auto lg:flex-col">
          {REPORTS.map((r) => (
            <button
              key={r.id}
              onClick={() => setName(r.id)}
              aria-current={name === r.id ? 'page' : undefined}
              className={`whitespace-nowrap rounded-lg px-3 py-2 text-left text-sm ${name === r.id ? 'bg-brand-600 text-white' : 'bg-surface text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50'}`}
            >
              {r.label}
            </button>
          ))}
        </nav>
        <div className="min-w-0 space-y-4">
          <Card>
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-40">
                <TextField
                  label="Dari"
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div className="w-40">
                <TextField
                  label="Sampai"
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
              <div className="flex gap-1">
                <Button variant="secondary" onClick={() => preset(1)}>
                  Hari ini
                </Button>
                <Button variant="secondary" onClick={() => preset(7)}>
                  7 hari
                </Button>
                <Button variant="secondary" onClick={() => preset(30)}>
                  30 hari
                </Button>
                <Button variant="secondary" onClick={() => preset('month')}>
                  Bulan ini
                </Button>
              </div>
              <div className="w-44">
                <SelectField
                  label="Outlet"
                  value={effOutlet}
                  onChange={(e) => setOutletId(e.target.value)}
                >
                  {!('outlet' in def && def.outlet) && <option value="">Semua outlet</option>}
                  {outlets.data?.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </SelectField>
              </div>
              {'group' in def && def.group && (
                <div className="w-36">
                  <SelectField
                    label="Kelompok"
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value as typeof groupBy)}
                  >
                    <option value="day">Per hari</option>
                    <option value="week">Per pekan</option>
                    <option value="month">Per bulan</option>
                  </SelectField>
                </div>
              )}
            </div>
          </Card>
          {exp.error && <Alert>{exp.error}</Alert>}
          <Card
            title={t?.title ?? def.label}
            actions={
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  disabled={exp.busy || !t}
                  onClick={() => void exp.run('csv')}
                >
                  CSV
                </Button>
                <Button
                  variant="secondary"
                  disabled={exp.busy || !t}
                  onClick={() => void exp.run('xlsx')}
                >
                  XLSX
                </Button>
                <Button
                  variant="secondary"
                  disabled={exp.busy || !t}
                  onClick={() => void exp.run('pdf')}
                >
                  PDF
                </Button>
                <Button
                  variant="ghost"
                  disabled={exp.busy}
                  onClick={() => void exp.run('csv', 'transactions/export')}
                >
                  Semua transaksi (CSV)
                </Button>
              </div>
            }
          >
            {q.isLoading && <p className="text-sm text-slate-500">Memuat…</p>}
            {q.error && <Alert>{(q.error as Error).message}</Alert>}
            {t && (
              <>
                {t.meta.note && <p className="mb-3 text-xs text-slate-500">{t.meta.note}</p>}
                {chart && chart.length > 0 && (
                  <div className="mb-4">
                    <Bars data={chart} />
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b text-left text-xs uppercase text-slate-500">
                      <tr>
                        {t.columns.map((c) => (
                          <th
                            key={c.key}
                            className={`px-2 py-2 ${c.type === 'text' ? '' : 'text-right'}`}
                          >
                            {c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {t.rows.length === 0 && (
                        <tr>
                          <td
                            colSpan={t.columns.length}
                            className="px-2 py-8 text-center text-slate-500"
                          >
                            Tidak ada data pada periode ini.
                          </td>
                        </tr>
                      )}
                      {t.rows.map((r, i) => (
                        <tr key={i}>
                          {t.columns.map((c) => (
                            <td
                              key={c.key}
                              className={`px-2 py-1.5 ${c.type === 'text' ? '' : 'text-right tabular-nums'}`}
                            >
                              {fmtCell(c, r[c.key] ?? null)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                    {t.totals && (
                      <tfoot className="border-t-2 font-semibold">
                        <tr>
                          {t.columns.map((c) => (
                            <td
                              key={c.key}
                              className={`px-2 py-2 ${c.type === 'text' ? '' : 'text-right tabular-nums'}`}
                            >
                              {fmtCell(c, t.totals![c.key] ?? null)}
                            </td>
                          ))}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                {money && <span className="sr-only">Laporan dalam rupiah</span>}
              </>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
